// اجزای مشترک صفحه‌های وسایط نقلیه
import React, { useState, useEffect, useRef } from 'react';
import { ui } from '../../components/ListPage';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import WarningDialog from '../../components/WarningDialog';
import { jsonOf, netErr } from '../../utils/http';

export const BASE = '/api/tadarokat/cars';

export const API = (path, opts = {}) => fetch(`${BASE}${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

export const qs = (o) => Object.entries(o || {})
  .filter(([, v]) => v !== undefined && v !== null && v !== '')
  .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

export const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
export const fmtAmt = n => Number(n || 0).toLocaleString('fa-IR');
export const curYear = () => {
  const y = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/')[0];
  return parseInt(y, 10) || 1404;
};
export const todayFa = () => {
  const p = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/');
  return `${p[0]}/${String(p[1]).padStart(2, '0')}/${String(p[2]).padStart(2, '0')}`;
};

const isEmpty = v => v === null || v === undefined || v === '' || v === 0 || v === '0';
const isNum = v => typeof v === 'number';

/** ستون‌هایی که در همهٔ ردیف‌ها خالی‌اند پنهان می‌شوند */
export function visibleColumns(columns, rows) {
  if (!rows || rows.length === 0) return columns || [];
  return (columns || []).filter(c => rows.some(r => !isEmpty(r[c])));
}

/** ستون‌ها برای اکسل */
export const excelCols = (columns, rows) =>
  visibleColumns(columns, rows).map(c => ({ key: c, label: c, num: rows.some(r => isNum(r[c])) }));

/** جدول عمومی با عنوان ستون‌های فارسی */
export function Grid({ columns, rows, rowKey, selectedKey, onSelect, onDoubleClick, rowBg, hideEmpty = true, empty = 'رکوردی یافت نشد', moneyCols = [] }) {
  const cols = hideEmpty ? visibleColumns(columns, rows) : (columns || []);
  return (
    <>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <tr>{cols.map(c => <th key={c} style={ui.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => {
            const k = rowKey ? r[rowKey] : i;
            const sel = selectedKey !== undefined && selectedKey !== null && k === selectedKey;
            const base = rowBg ? rowBg(r, i) : null;
            const bg = sel ? '#e8f0fe' : (base || (i % 2 ? '#fdf6ec' : '#fff'));
            return (
              <tr key={k ?? i} style={{ background: bg, cursor: onSelect ? 'pointer' : 'default', outline: sel ? '2px solid #1a2f50' : 'none' }}
                onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onDoubleClick && onDoubleClick(r)}>
                {cols.map(c => {
                  const v = r[c];
                  const money = moneyCols.includes(c);
                  const txt = isNum(v) ? (money ? fmtAmt(v) : toFa(v)) : (v ?? '');
                  return <td key={c} style={{ ...(isNum(v) ? ui.tdNum : ui.td), whiteSpace: 'pre-line' }}>{txt}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {(!rows || rows.length === 0) && <div style={ui.empty}>{empty}</div>}
    </>
  );
}

/** فیلد با برچسب */
export function Field({ label, children, flex = 1, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: width ? 'none' : flex, width, minWidth: '120px' }}>
      <label style={ui.mlabel}>{label}</label>
      {children}
    </div>
  );
}

export function TextIn({ value, onChange, disabled, num, ...rest }) {
  return <input value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)}
    style={{ ...ui.input, ...(num ? { direction: 'ltr', textAlign: 'center' } : {}), background: disabled ? '#f3f3f3' : '#fff' }} {...rest} />;
}

export function DateIn({ value, onChange, disabled }) {
  return <JalaliDatePicker value={value || ''} onChange={v => onChange(v)} disabled={disabled} />;
}

/** جستجو و انتخاب از فهرست */
export function Lookup({ path, value, onChange, disabled, placeholder = 'جستجو...' }) {
  const [text, setText] = useState(value?.title || '');
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(value?.title || ''); }, [value?.id, value?.title]);
  const search = (q) => {
    setText(q);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const j = await API(`${path}?q=${encodeURIComponent(q)}`);
      setList(j.ok ? j.data : []);
      setOpen(true);
    }, 300);
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input value={text} disabled={disabled} placeholder={placeholder}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          style={{ ...ui.input, background: disabled ? '#f3f3f3' : '#fff' }} />
        {!disabled && value?.id ? <button type="button" onClick={() => { onChange({ id: 0, title: '' }); setText(''); }}
          style={{ ...ui.btnCancel, padding: '4px 10px' }}>✕</button> : null}
      </div>
      {open && list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #ccc', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto', width: '100%', boxShadow: '0 6px 18px rgba(0,0,0,0.15)' }}>
          {list.map(o => (
            <div key={o.id} onMouseDown={() => { onChange({ id: o.id, title: o.title }); setText(o.title); setOpen(false); }}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}>
              {toFa(o.display || o.title)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** پیشنهاد شرح‌های قبلی */
export function SuggestIn({ path, value, onChange, disabled }) {
  const [list, setList] = useState([]);
  const timer = useRef(null);
  const change = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (!v || v === '0') { setList([]); return; }
    timer.current = setTimeout(async () => {
      const j = await API(`${path}${path.includes('?') ? '&' : '?'}q=${encodeURIComponent(v)}`);
      setList(j.ok ? j.data : []);
    }, 400);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input value={value ?? ''} disabled={disabled} onChange={e => change(e.target.value)}
        onBlur={() => setTimeout(() => setList([]), 200)} style={{ ...ui.input, background: disabled ? '#f3f3f3' : '#fff' }} />
      {list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #ccc', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', width: '100%' }}>
          {list.map((s, i) => <div key={i} onMouseDown={() => { onChange(s); setList([]); }}
            style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '12px' }}>{s}</div>)}
        </div>
      )}
    </div>
  );
}

/** پوستهٔ مودال */
export function Modal({ title, onClose, children, footer, width = '760px' }) {
  return (
    <div style={ui.overlay}>
      <div style={{ ...ui.modal, maxWidth: width }}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={ui.mBody}>{children}</div>
        {footer && <div style={ui.mFooter}>{footer}</div>}
      </div>
    </div>
  );
}

/** پرسش حذف */
export function DeleteDialog({ onConfirm, onCancel }) {
  return <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر"
    tone="danger" onConfirm={onConfirm} onCancel={onCancel} />;
}

/** پیام ساده */
export function MsgDialog({ text, onClose }) {
  return <WarningDialog title="پیام" lines={String(text || '').split('\n').filter(Boolean)} confirmLabel="تایید" cancelLabel="بستن"
    onConfirm={onClose} onCancel={onClose} />;
}

export const smallBtn = (bg) => ({
  padding: '6px 12px', background: bg || 'linear-gradient(135deg,#1a237e,#283593)', color: '#fff', border: 'none',
  borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
});

export const groupBox = {
  display: 'flex', flexDirection: 'column', gap: '4px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '4px 8px',
};
export const groupTitle = { fontSize: '10px', fontWeight: 700, color: '#1a2f50' };
